import classnames from 'classnames'
import { ReactElement, useMemo } from 'react'

// Domains
import ContentfulProductMeasuringGuide from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import keyBy from '@src/quince-site-common/src/utils/keyBy'

import useLockBodyScroll from '../modal/hooks/useLockBodyScroll'
import SizeChart from '../sizeChart'
import SizeChartHeader from '../sizeChart/components/SizeChartHeader.component'
import SizeChartMergedSizeChartTabs from '../sizeChart/components/SizeChartMergedSizeChartTabs.component'
import Cross from '../sizeChart/icons/Cross.inline.svg'
import styles from '../sizeChart/sizeChart.module.scss'

import { PDPProductSizeGuide, ProductSizeGuide, ProductSizeGuideDesignVariant } from './SizeGuide.types'

// Components
import { SizeGuideOne } from './sizeGuideOne'
import { SizeGuideTwo } from './sizeGuideTwo'

// Hooks

import { useSizeGuide } from './useSizeGuide'

// Icons

// Styles

interface SizeGuideProps {
  sizeChart: ContentfulSizeChart
  mergedSizeChart?: MergedSizeChart[]
  measuringGuides?: ContentfulProductMeasuringGuide[]
  wrapperClassName?: string
  showSizeChartModal: boolean
  onSizeChartModalClose: () => void
}

const SizeGuide = (props: SizeGuideProps) => {
  const pdpProductSizeGuides: PDPProductSizeGuide[] = getPdpProductSizeGuides(
    props.sizeChart,
    props.mergedSizeChart || [],
    props.measuringGuides || [],
  )

  const { activeSizeGuideUnitIndex, handleUnitsChange, activePdpProductSizeGuide, handlePdpProductSizeGuideChange } =
    useSizeGuide({
      pdpProductSizeGuides,
    })

  const productSizeGuides: ProductSizeGuide[] =
    pdpProductSizeGuides.find(ppsz => ppsz.tabName === activePdpProductSizeGuide)?.productSizeGuides || []

  const activeProductSizeGuide = productSizeGuides[0]

  const productSizeGuideDesignVariant = useMemo(() => {
    return getProductSizeGuideDesignVariant(productSizeGuides)
  }, [productSizeGuides])

  const activeSizeChartSection = activeProductSizeGuide.sizeChart.sections.find(
    (sc, scIdx) => scIdx === activeSizeGuideUnitIndex,
  )
  const isUnitsToggleActive = !!(
    activeSizeChartSection?.title &&
    activeSizeChartSection.title === activeProductSizeGuide.sizeChart.sections[1]?.title
  )

  const shouldHideSizeChart = !props.showSizeChartModal || !productSizeGuides.length || !activeSizeChartSection

  useLockBodyScroll(!shouldHideSizeChart)

  if (shouldHideSizeChart) return null

  return (
    <>
      <div onClick={props.onSizeChartModalClose} className={styles.blurModal} />
      <div className={classnames(styles.wrapper, props.wrapperClassName)}>
        <Cross aria-label="close-button" className={styles.crossIcon} onClick={props.onSizeChartModalClose} />

        <SizeChartHeader
          activeSizeChart={activeProductSizeGuide.sizeChart}
          sizeChartSections={activeProductSizeGuide.sizeChart.sections}
          selectedSizeChartSection={activeSizeChartSection}
          isMergedSizeChart={pdpProductSizeGuides.length > 1}
          isUnitsToggleActive={isUnitsToggleActive}
          onUnitsToggle={() => {
            handleUnitsChange(activeSizeGuideUnitIndex ? 0 : 1)
          }}
          hideUnitsTitle
        />

        {pdpProductSizeGuides.length > 1 && (
          <SizeChartMergedSizeChartTabs
            activePdpProductSizeGuide={activePdpProductSizeGuide}
            handlePdpProductSizeGuideChange={handlePdpProductSizeGuideChange}
            pdpProductSizeGuides={pdpProductSizeGuides}
          />
        )}

        {renderProductSizeGuide({
          productSizeGuides,
          productSizeGuideDesignVariant,
          activeSizeGuideUnitIndex,
        })}
      </div>
    </>
  )
}

const getPdpProductSizeGuides = (
  sizeChart: ContentfulSizeChart,
  mergedSizeChart: MergedSizeChart[],
  measuringGuides: ContentfulProductMeasuringGuide[],
): PDPProductSizeGuide[] => {
  const pdpProductSizeGuides = []
  if (mergedSizeChart.length > 0) {
    mergedSizeChart.forEach(msc => {
      pdpProductSizeGuides.push({
        tabName: msc.tabName,
        productSizeGuides: getProductSizeGuides(msc.sizeChart, measuringGuides),
      })
    })
  } else {
    pdpProductSizeGuides.push({
      tabName: 'pdpProductSizeGuide',
      productSizeGuides: getProductSizeGuides(sizeChart, measuringGuides),
    })
  }
  return pdpProductSizeGuides
}

const getProductSizeGuides = (
  productSizeChart: ContentfulSizeChart,
  productMeasuringGuides: ContentfulProductMeasuringGuide[],
): ProductSizeGuide[] => {
  const productMeasuringGuideKeys = productSizeChart.measuringGuides
  const productMeasuringGuidesByKey: Record<string, ContentfulProductMeasuringGuide> = keyBy(
    productMeasuringGuides,
    'title',
  )
  return productMeasuringGuideKeys?.length
    ? productMeasuringGuideKeys.map(msk => ({
        sizeChart: productSizeChart,
        measuringGuide: productMeasuringGuidesByKey[msk],
      }))
    : [
        {
          sizeChart: productSizeChart,
          measuringGuide: undefined,
        },
      ]
}

const getProductSizeGuideDesignVariant = (
  productSizeGuides: ProductSizeGuide[],
): ProductSizeGuideDesignVariant | undefined => {
  let psgVariant
  if (productSizeGuides.length > 1) {
    psgVariant = ProductSizeGuideDesignVariant.DESIGN_2
  } else if (productSizeGuides[0].measuringGuide) {
    psgVariant = ProductSizeGuideDesignVariant.DESIGN_1
  }
  return psgVariant
}

const renderProductSizeGuide = ({
  productSizeGuides,
  productSizeGuideDesignVariant,
  activeSizeGuideUnitIndex,
}: {
  productSizeGuides: ProductSizeGuide[]
  productSizeGuideDesignVariant?: ProductSizeGuideDesignVariant
  activeSizeGuideUnitIndex: number
}): ReactElement | null => {
  switch (productSizeGuideDesignVariant) {
    case ProductSizeGuideDesignVariant.DESIGN_1:
      return <SizeGuideOne productSizeGuides={productSizeGuides} activeSizeGuideUnitIndex={activeSizeGuideUnitIndex} />
    case ProductSizeGuideDesignVariant.DESIGN_2:
      return <SizeGuideTwo productSizeGuides={productSizeGuides} activeSizeGuideUnitIndex={activeSizeGuideUnitIndex} />
    default:
      return <SizeChart productSizeGuides={productSizeGuides} activeSizeGuideUnitIndex={activeSizeGuideUnitIndex} />
  }
}

export default SizeGuide
